import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/config/constants'
import { useAuthStore } from '@/stores/authStore'
import type { QueryClient } from '@tanstack/react-query'

// Set by App.tsx after queryClient is created — avoids circular imports
let _queryClient: QueryClient | null = null
export const setQueryClient = (qc: QueryClient) => { _queryClient = qc }

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // 10s is long enough for reasonable queries but short enough to surface
  // backend hangs as errors instead of showing an infinite spinner.
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear all cached queries so no page is stuck with a stale loading state
      _queryClient?.clear()
      // Clear auth via store — ProtectedRoute will handle the redirect
      useAuthStore.getState().clearAuth()
    }
    return Promise.reject(error)
  }
)
